import { css } from '@emotion/core';
import {
  halfSpacing, largeSpacing, lectureBottomPanelZIndex,
  standardSpacing, resourceCenterPendoBadgeZIndex,
} from 'styles/global_defaults/scaffolding';
import { useContext, useEffect } from 'react';
import ClickableContainer from 'components/clickable-container';
import { useSelector } from 'react-redux';
import t from 'react-translate';
import { setLeftPanelVisibility, setTimelineIsExpanded } from 'redux/actions/lecture-pages';
import { useAppDispatch } from 'redux/store';
import NvIcon from 'shared/components/nv-icon';
import NvTooltip from 'shared/components/nv-tooltip';
import { gray2, hexToRgbaString, primary, yellow } from 'styles/global_defaults/colors';
import { getCurrentCourse, getFlatCourseAliases } from 'redux/selectors/course';
import { screenSmMax, screenXsMax } from 'styles/global_defaults/media-queries';
import { useLecturePageLink, useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';
import BookmarkIcon from 'bookmarks/components/bookmark-icon';
import { createBookmark, highlightBookmark } from 'redux/actions/bookmarks';
import { BookmarkType } from 'redux/schemas/models/bookmark';
import { AngularServicesContext } from 'react-app';
import { useMediaQuery } from 'react-responsive';
import { getCurrentInstitution } from 'redux/selectors/institutions';
import CourseOutlineProgressBar from './course-outline-progress-bar';
import { config } from '../../../config/pendo.config.json';
import AutomatedTranslationTranslator from './automated-translation-translator';
import { LecturePageMode } from '.';


/** Contains Back/Forward lecture buttons and a progress bar showing current progress through the course */
export const LecturePageBottomPanel = () => {
  const { mode } = useLecturePageParams();
  const currentCourse = useSelector(getCurrentCourse);
  const currentInstitution = useSelector(getCurrentInstitution);

  const isNotDesktop = useMediaQuery({
    query: `(max-width: ${screenSmMax}px)`,
  });

  const isHandheld = useMediaQuery({
    query: `(max-width: ${screenXsMax}px)`,
  });

  const styles = css`
    position: fixed;
    bottom: 0;
    /* Set the width based on the LHS nav view. The LHS only shows on desktop      .
     * screens. So reducing the LHS width from total width in desktop screens and
     * using full width on non desktop screens */
    width: calc(100vw - 60px);
    ${isNotDesktop && css`
      width: 100%;
    `}
    height: 60px;
    background-color: ${hexToRgbaString(gray2, 0.8)};
    z-index: ${lectureBottomPanelZIndex};

    display: flex;
    align-items: center;

    .previous, .next {
      position: absolute;
      border: 0px solid transparent;
    }

    .previous {
      right: 50%;
      border-right-width: 1px;
    }

    .next {
      left: 50%;
      border-left-width: 1px;
    }

    .toggle-button-container {
      user-select: false;

      :hover .outline-icon {
        background: ${gray2};
      }
    }

    .automated-translation-container {
      display: flex;
      align-items: center;
      margin-right: ${largeSpacing}px;

      .icon-warning {
        color: ${yellow};
      }
    }
  `;

  const dispatch = useAppDispatch();
  const { isLeftPanelVisible, translationFailed } = useSelector(state => state.app.lecturePage);

  const { FlyoutModalManager } = useContext(AngularServicesContext);

  const toggleLeftPanelVisible = () => {
    const newVisibility = !isLeftPanelVisible;
    dispatch(setLeftPanelVisibility(newVisibility));

    if (!isNotDesktop) {
      dispatch(setTimelineIsExpanded(newVisibility));
    }
    // Added because for some reason is not being called automatically on colored dev servers, even if
    // it's running in local dev mode.
    window.dispatchEvent(new Event('resize'));
  };

  const params = useLecturePageParams();
  const lectureLink = useLecturePageLink();

  const courseAliases = useSelector(state => getFlatCourseAliases(state, params.catalogId));
  const lecturePage = useSelector(state => state.models.lecturePages[params.lecturePageId]);
  const isLectureLoaded = useSelector(state => lecturePage && state.app.lecturePage.isCurrentLectureLoaded);

  const navigateBack = isLectureLoaded && lecturePage.previousPage && (() => {
    lectureLink({
      ...params,
      lecturePageId: lecturePage.previousPage.id,
    });
  });

  const navigateForward = isLectureLoaded && lecturePage.nextPage && (() => {
    lectureLink({
      ...params,
      lecturePageId: lecturePage.nextPage.id,
    });
  });

  const onCreateBookmark = () => {
    dispatch(createBookmark({
      type: BookmarkType.LECTURE_PAGE,
      catalogId: params.catalogId,
      componentId: params.lecturePageId,
    })).then(() => {
      FlyoutModalManager.openFlyout({
        controller: 'BookmarksFlyoutCtrl as vm',
        template: 'bookmarks/templates/bookmarks-flyout-react-app.html',
        isNavigational: false,
      });
    });
  };

  const onHighlightBookmark = () => {
    dispatch(highlightBookmark({ id: lecturePage.bookmarkId }));
    FlyoutModalManager.openFlyout({
      controller: 'BookmarksFlyoutCtrl as vm',
      template: 'bookmarks/templates/bookmarks-flyout-react-app.html',
      isNavigational: false,
    });
  };

  // TODO: The prod app has an iframe covering this bottom panel. Why?
  return (
    <div css={styles}>
      <CourseOutlineProgressBar />
      <div className='d-flex justify-content-between w-100 align-items-center h-100'>
        <div className='toggle-button-container pl-4'>
          {/* TODO: Open/closed tooltip */}
          <NvTooltip text={isLeftPanelVisible ? t.LECTURE_PAGES.HIDE_OUTLINE(courseAliases) : t.LECTURE_PAGES.SHOW_OUTLINE(courseAliases)}>
            <ClickableContainer
              onClick={() => toggleLeftPanelVisible()}
              data-qa={config.pendo.lecturePage.openCloseIconOutline}
            >
              <div className='d-flex align-items-center'>
                <div
                  className={`outline-icon ${isLeftPanelVisible ? 'bg-primary' : ''} text-white rounded-circle p-2`}
                  data-qa={config.pendo.lecturePage[isLeftPanelVisible ? 'closeOutline' : 'openOutline']}
                >
                  <NvIcon size='medium' icon='course-menu' />
                </div>
                {!isHandheld && (
                  <div className='pl-2 text-white course-title-xs'>
                    {t.LECTURE_PAGES.OUTLINE()}
                  </div>
                )}
              </div>
            </ClickableContainer>
          </NvTooltip>
        </div>
        <div
          className='text-white h-100 w-100'
          data-qa={config.pendo.lecturePage.navigationArrows}
        >
          <NvTooltip text={lecturePage?.previousPage && t.LECTURE_PAGES.PREVIOUS_LECTURE(lecturePage.previousPage.title)} enabled={!!lecturePage?.previousPage}>
            {/* This inner div must be present in order for React to insert these NvTooltips. I'm unsure
            as to why but it seems like an issue with React.Fragment */}
            <div className='previous' data-qa={config.pendo.lecturePage.previousPage}>
              <NavigationButton direction='previous' navigate={navigateBack} />
            </div>
          </NvTooltip>
          <NvTooltip text={lecturePage?.nextPage && t.LECTURE_PAGES.NEXT_LECTURE(lecturePage.nextPage.title)} enabled={!!lecturePage?.nextPage}>
            <div className='next' data-qa={config.pendo.lecturePage.nextPage}>
              <NavigationButton direction='next' navigate={navigateForward} />
            </div>
          </NvTooltip>
        </div>
        {mode === LecturePageMode.VIEW
          && currentCourse.automatedTranslationEnabled
          && currentInstitution.automatedTranslationEnabled
          && (
            <div className='automated-translation-container'>
              {translationFailed && (
                <NvTooltip text={t.AUTOMATED_TRANSLATION.ERROR_RETRY()}>
                  <NvIcon
                    icon='warning'
                    size='xss-smallest'
                    className='translation-alert mr-2'
                  />
                </NvTooltip>
              )}
              <AutomatedTranslationTranslator />
            </div>
          )}
        <div className='pr-4'>
          <BookmarkIcon
            size='medium'
            bookmarked={!!lecturePage?.bookmarkId}
            onCreate={onCreateBookmark}
            onHighlight={onHighlightBookmark}
            createTooltip={t.LHS.BOOKMARKS.ADD_BOOKMARK.LESSON(courseAliases)}
            tooltipZIndex={resourceCenterPendoBadgeZIndex + 1}
            colors={{
              default: 'white',
              bookmarked: 'white',
            }}
          />
        </div>
      </div>
    </div>
  );
};

const NavigationButton = (props: { direction: 'previous' | 'next', navigate: () => void }) => {
  const isNotDesktop = useMediaQuery({
    query: `(max-width: ${screenSmMax}px)`,
  });

  const isHandheld = useMediaQuery({
    query: `(max-width: ${screenXsMax}px)`,
  });

  const styles = css`
    ${isHandheld && css`
      width: 60px;
    `}
    height: 60px;
    display: flex;
    flex-direction: ${props.direction === 'previous' ? 'row' : 'row-reverse'};
    background-color: ${props.direction === 'previous' ? gray2 : primary};
    justify-content: center;
    align-items: center;
    user-select: none;
    opacity: .8;
    border: hidden;
    border-radius: 0;
    outline: none;
    padding-left: ${standardSpacing}px;
    padding-right: ${standardSpacing}px;

    .icon {
      ${!isNotDesktop && css`
        ${props.direction === 'previous' && css`
          margin-right: ${halfSpacing}px;
        `};
        ${props.direction === 'next' && css`
          margin-left: ${halfSpacing}px;
        `};
      `};
    }

    :not(.disabled) {
      cursor: pointer;
    }

    &:hover:not(.disabled) {
      opacity: 1.0;
    }

    &.disabled {
      background-color: ${gray2};
      opacity: .5;

      ${isHandheld && css`
        opacity: .3;
      `}
    }
  `;

  return (
    <ClickableContainer onClick={() => props.navigate?.()} disabled={!props.navigate}>
      <div css={styles} className={`icon-container ${!props.navigate ? 'disabled' : ''}`}>
        <NvIcon size='small' icon={props.direction === 'previous' ? 'arrow-left' : 'arrow-right'} />
        {!isHandheld && (
          <div className='course-title-xs'>
            {(props.direction === 'previous' ? t.SHARED.PREVIOUS() : t.SHARED.NEXT()).toUpperCase()}
          </div>
        )}
      </div>
    </ClickableContainer>
  );
};


export default LecturePageBottomPanel;

